import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Award, Loader2, X } from "lucide-react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/utils/utils";
import ReportPeriodBar from "../shared/ReportPeriodBar";
import ReportFailureState from "../shared/ReportFailureState";
import { formatRunSummary, getDefaultPeriod } from "../shared/period";
import { useTradeLeaderboards } from "./hooks/useTradeLeaderboards";
import LeaderboardCard, {
  LeaderboardMember,
} from "./components/LeaderboardCard";
import SignatureControls from "./components/SignatureControls";
import { TierFilter } from "./utils/buildLeaderboards";
import {
  CertificateData,
  certificateKey,
  CertificateSignature,
  DEFAULT_SIGNATURE_NAME,
  parseCertificateKey,
} from "./certificates/certificateData";
import { downloadCertificates } from "./certificates/downloadCertificates";
import { celebrate } from "./utils/celebrate";

const TIER_OPTIONS: { value: TierFilter; label: string }[] = [
  { value: "all", label: "All Tiers" },
  { value: "2", label: "Tier 2" },
  { value: "1", label: "Tier 1" },
];

const TradeLeaderboards: React.FC = () => {
  const defaults = getDefaultPeriod();
  const [year, setYear] = useState(defaults.year);
  const [month, setMonth] = useState(defaults.month);
  const [activeTrade, setActiveTrade] = useState("");

  const {
    failed,
    hasRun,
    leaderboards,
    loading,
    meta,
    run,
    runNonce,
    setTier,
    tier,
  } = useTradeLeaderboards();

  const trades = leaderboards.map((entry) => entry.trade);
  const currentTrade = trades.includes(activeTrade)
    ? activeTrade
    : (trades[0] ?? "");

  const monthLabel = meta?.monthLabel ?? "";

  // The period-bar summary must count what's actually on screen. `meta.count` is
  // the whole month (unfiltered), so when a tier filter is active it overstates
  // the displayed records. Every row lands in exactly one card, and a card's
  // `totalMembers` is that group's row count, so summing them across the
  // (already tier-filtered) leaderboards yields the record count for the current
  // view — and equals `meta.count` when the filter is "All Tiers".
  const recordCount = useMemo(
    () =>
      leaderboards.reduce(
        (tradeSum, trade) =>
          tradeSum +
          trade.cards.reduce((cardSum, card) => cardSum + card.totalMembers, 0),
        0,
      ),
    [leaderboards],
  );

  // Members selected for certificate printing, as certificateKeys.
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Surfaces a failed bulk certificate generation (no silent swallow).
  const [bulkFailed, setBulkFailed] = useState(false);

  // Signer for generated certificates. SignatureControls owns the state and
  // publishes here, so typing a name never re-renders the leaderboards.
  const signatureRef = useRef<CertificateSignature>({
    name: DEFAULT_SIGNATURE_NAME,
  });

  // Selection is scoped to the visible trade + tier + run; reset on change.
  // Keyed on `runNonce` (not `monthLabel`) for the run dimension so that
  // re-running the SAME month still clears stale selections — `monthLabel` is
  // unchanged on a same-month re-run, but `runNonce` always advances.
  useEffect(() => {
    setSelected(new Set());
    setBulkFailed(false);
  }, [currentTrade, tier, runNonce]);

  const toggleSelect = useCallback(
    (category: string, fullName: string, company: string) => {
      setSelected((prev) => {
        const next = new Set(prev);
        const key = certificateKey(category, fullName, company);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      });
    },
    [],
  );

  const toggleCardAll = useCallback(
    (category: string, members: LeaderboardMember[], select: boolean) => {
      setSelected((prev) => {
        const next = new Set(prev);
        for (const member of members) {
          const key = certificateKey(category, member.fullName, member.company);
          if (select) {
            next.add(key);
          } else {
            next.delete(key);
          }
        }
        return next;
      });
    },
    [],
  );

  const handleDownloadCertificate = useCallback(
    async (category: string, fullName: string) => {
      // Confetti fires only after the PDF has actually been produced, so a
      // failed render (surfaced by the caller's catch) never celebrates.
      await downloadCertificates(
        [{ fullName, category, month: monthLabel }],
        signatureRef.current,
      );
      // The download has already succeeded here; the confetti flourish is
      // best-effort. Guard it so a canvas-confetti failure can't propagate out
      // of this promise and be misreported as a download failure by
      // LeaderboardCard's try/catch.
      try {
        celebrate();
      } catch {
        // Swallow — the certificate downloaded fine; only the confetti failed.
      }
    },
    [monthLabel],
  );

  const handleBulkPrint = useCallback(async () => {
    if (selected.size === 0) {
      return;
    }
    setBulkBusy(true);
    setBulkFailed(false);
    try {
      const items: CertificateData[] = [...selected]
        .map((key) => {
          const { category, fullName } = parseCertificateKey(key);
          return { category, fullName, month: monthLabel };
        })
        .sort(
          (a, b) =>
            a.category.localeCompare(b.category) ||
            a.fullName.localeCompare(b.fullName),
        );
      await downloadCertificates(items, signatureRef.current);
      // The batch PDF has already downloaded here; the confetti flourish is
      // best-effort. Guard it (mirroring the single-cert path) so a
      // canvas-confetti failure can't propagate to the catch below and be
      // misreported as a bulk print failure that sets `bulkFailed`.
      try {
        celebrate();
      } catch {
        // Swallow — the certificates downloaded fine; only the confetti failed.
      }
    } catch {
      setBulkFailed(true);
    } finally {
      setBulkBusy(false);
    }
  }, [selected, monthLabel]);

  const handleRun = () => {
    run(Number(year), Number(month));
  };

  return (
    <MainPageWrapper title="Leaderboards">
      <div className="space-y-6">
        <ReportPeriodBar
          loading={loading}
          month={month}
          onMonthChange={setMonth}
          onRun={handleRun}
          onYearChange={setYear}
          summary={
            hasRun && meta
              ? formatRunSummary(recordCount, "record", meta.monthLabel)
              : undefined
          }
          year={year}
        >
          <SignatureControls signatureRef={signatureRef} />
        </ReportPeriodBar>

        {failed ? (
          <ReportFailureState onRetry={handleRun} />
        ) : !hasRun ? (
          <div className="rounded-md border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500 dark:border-slate-700 dark:text-slate-400">
            Select a month and year, then click{" "}
            <span className="font-medium">Run Report</span> to view the trade
            leaderboards.
          </div>
        ) : (
          <div className="space-y-4">
            {/* The tier toggle stays mounted for any completed run so filtering
                to an empty tier can always be undone (the empty-state message
                renders below it, never in its place). */}
            <div className="flex items-center gap-1 rounded-md bg-light p-1 w-fit dark:bg-slate-800">
              {TIER_OPTIONS.map((option) => (
                <button
                  className={cn(
                    "rounded px-3 py-1.5 text-sm font-medium transition-colors",
                    tier === option.value
                      ? "bg-white text-fontColor shadow-sm dark:bg-slate-700 dark:text-white"
                      : "text-gray-500 hover:text-gray-800 dark:text-slate-400 dark:hover:text-slate-200",
                  )}
                  key={option.value}
                  onClick={() => setTier(option.value)}
                  type="button"
                >
                  {option.label}
                </button>
              ))}
            </div>

            {leaderboards.length === 0 ? (
              <div className="rounded-md border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500 dark:border-slate-700 dark:text-slate-400">
                {/* The month has records but none match the active tier filter —
                    say so, rather than implying the whole month is empty. When
                    the month itself is empty (count 0, or filter cleared) fall
                    back to the no-data wording. */}
                {tier !== "all" && meta && meta.count > 0
                  ? "No results for this tier."
                  : "No scoreboard records found for the selected month."}
              </div>
            ) : (
              <>
                {selected.size > 0 && (
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-primary/30 bg-primary/5 px-4 py-2.5 dark:border-primary/40 dark:bg-primary/10">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm font-medium text-gray-700 dark:text-slate-200">
                        {selected.size} member{selected.size === 1 ? "" : "s"}{" "}
                        selected
                      </span>
                      {bulkFailed && (
                        <span className="text-xs text-red-600 dark:text-red-400">
                          Certificate generation failed — please try again.
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        disabled={bulkBusy}
                        onClick={() => {
                          setSelected(new Set());
                          // Clearing the selection also dismisses any lingering
                          // bulk-failure notice — the selection-reset effect
                          // only fires on trade/tier/run changes, so a stale
                          // failure would otherwise persist across a re-select.
                          setBulkFailed(false);
                        }}
                        size="sm"
                        variant="ghost"
                      >
                        <X className="mr-1 h-3.5 w-3.5" />
                        Clear
                      </Button>
                      <Button
                        disabled={bulkBusy}
                        onClick={handleBulkPrint}
                        size="sm"
                      >
                        {bulkBusy ? (
                          <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                        ) : (
                          <Award className="mr-1.5 h-4 w-4" />
                        )}
                        {bulkBusy
                          ? "Generating…"
                          : `Print ${selected.size} certificate${
                              selected.size === 1 ? "" : "s"
                            }`}
                      </Button>
                    </div>
                  </div>
                )}

                <Tabs onValueChange={setActiveTrade} value={currentTrade}>
                  <TabsList>
                    {leaderboards.map((entry) => (
                      <TabsTrigger key={entry.trade} value={entry.trade}>
                        {entry.trade}
                      </TabsTrigger>
                    ))}
                  </TabsList>

                  {leaderboards.map((entry) => (
                    <TabsContent key={entry.trade} value={entry.trade}>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {entry.cards.map((card) => (
                          <LeaderboardCard
                            card={card}
                            key={card.category}
                            monthLabel={monthLabel}
                            onDownloadCertificate={handleDownloadCertificate}
                            onToggleCardAll={toggleCardAll}
                            onToggleSelect={toggleSelect}
                            selectedKeys={selected}
                          />
                        ))}
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              </>
            )}
          </div>
        )}
      </div>
    </MainPageWrapper>
  );
};

export default TradeLeaderboards;
